import type {JSX, ReactElement, ReactNode} from 'react'
import React, {Children, isValidElement, cloneElement} from 'react'
import Image from 'next/image'
import {Faq} from '@/products/flexible-web-toolkit/blocks/faq'
import {FaqItem} from '@/products/flexible-web-toolkit/blocks/faq-item'

interface BaseProps {
	className?: string
	children: ReactNode
	title?: string
}

type WeightProps = BaseProps

export const Weight: React.FC<WeightProps> = ({className = '', children}) => {
	return (
		<div className={`flex flex-col justify-between ${className}`}>
			{children}
		</div>
	)
}

interface PackProps extends BaseProps {
	noBorder?: boolean
	title?: string
}

export const Pack: React.FC<PackProps> = ({
	className = '',
	children,
	noBorder = false,
}) => {
	return (
		<div
			className={`flex flex-col justify-between ${
				noBorder ? '' : 'xl:border-l'
			} border-gray-300 ${className}`}
		>
			{children}
		</div>
	)
}

type ImageExtraDetailsProps = BaseProps

const ImageExtraDetails: React.FC<ImageExtraDetailsProps> = ({
	className = '',
	children,
}) => {
	return (
		<div className={`flex justify-center bg-gray-100 py-6 ${className}`}>
			{children}
		</div>
	)
}

interface DisclaimerProps {
	className?: string
	disclaimerNotes: string[]
}

const Disclaimer: React.FC<DisclaimerProps> = ({
	className = '',
	disclaimerNotes,
}) => {
	return (
		<ul className={`${className} mt-8 list-none`}>
			{disclaimerNotes.map((item) => (
				<li className="mb-2" key={item}>
					<cite>{item}</cite>
				</li>
			))}
		</ul>
	)
}

interface ProductHeaderProps extends BaseProps {
	expandFirst?: boolean
}

export const ProductHeader: React.FC<ProductHeaderProps> = (props) => {
	const {className = '', children} = props

	const childrenArray = Children.toArray(children)

	const image = childrenArray.filter((child) => {
		return isValidElement(child) && child.type === Image
	}) as ReactElement[]

	const imageExtraDetails = childrenArray.filter((child) => {
		return isValidElement(child) && child.type === ImageExtraDetails
	}) as ReactElement[]

	const cardSection = childrenArray.filter((child) => {
		return isValidElement(child) && child.type === 'section'
	}) as ReactElement[]

	const weight = childrenArray.filter((child) => {
		return isValidElement(child) && child.type === Weight
	}) as ReactElement[]

	const pack = childrenArray.filter((child) => {
		return isValidElement(child) && child.type === Pack
	}) as ReactElement[]

	const disclaimer = childrenArray.filter((child) => {
		return isValidElement(child) && child.type === Disclaimer
	}) as ReactElement[]

	const headerText = childrenArray.filter((child) => {
		return (
			isValidElement(child) &&
			child.type !== 'section' &&
			child.type !== Image &&
			child.type !== Weight &&
			child.type !== Pack &&
			child.type !== ImageExtraDetails &&
			child.type !== Disclaimer
		)
	}) as JSX.Element[]

	return (
		<div className={`items-start md:flex ${className}`}>
			<div className="mb-2 overflow-hidden rounded-lg border border-gray-300 md:mb-0 md:mb-6 md:w-1/3">
				{cloneElement(
					headerText[0] as ReactElement<{className?: string}>,
					{
						className: 'px-4 pt-2 pb-0 md:sr-only',
					}
				)}

				<div className="p-4 md:p-8 lg:p-10">
					{cloneElement(
						image[0] as ReactElement<{className?: string}>,
						{className: 'mx-auto'}
					)}
				</div>
				{imageExtraDetails}
			</div>
			<div className="md:ml-16 md:w-2/3">
				<div className="content content--no-width-restriction px-4 md:p-0">
					{isValidElement(headerText[0]) ? (
						<div
							aria-hidden="true"
							className="hidden text-h1 font-bold md:block"
						>
							{
								(
									headerText[0] as ReactElement<{
										children?: ReactNode
									}>
								).props.children
							}
						</div>
					) : null}
					{headerText[1]}
					{cardSection.length > 0 && (
						<div className="my-10 hidden bg-gray-100 p-4 md:block">
							{cardSection}
						</div>
					)}

					{(weight.length > 0 || pack.length > 0) && (
						<div className="hidden px-4 md:flex md:flex-wrap">
							{weight} {pack}
						</div>
					)}
				</div>
				{disclaimer.length > 0 && (
					<div className="hidden md:block">{disclaimer}</div>
				)}
			</div>
			<div className="mt-8 md:hidden">
				<Faq>
					{[
						cardSection.length > 0 && (
							<FaqItem
								expanded={props.expandFirst}
								key="key-features"
								title="Key Features"
							>
								{cardSection}
							</FaqItem>
						),

						weight.length > 0 ? (
							<FaqItem
								key="weight"
								title={
									weight[0]
										? (
												weight[0] as React.ReactElement<{
													title?: string
												}>
											).props.title
										: null
								}
							>
								{weight}
							</FaqItem>
						) : null,
						...(pack.length > 0
							? pack.map((packChild, i) => (
									<FaqItem
										key={packChild.key ?? `pack-${i}`}
										title={
											(
												packChild as React.ReactElement<{
													title?: string
												}>
											).props.title
										}
									>
										{packChild}
									</FaqItem>
								))
							: []),
					].filter(Boolean)}
				</Faq>
				{disclaimer}
			</div>
		</div>
	)
}
